import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import styled from 'styled-components';
import { saleOffersApiRequests } from 'utils/api-requests/sale';
import SaleContext from 'utils/context/SaleContext';
import { decodeData } from 'utils/encodeAndDecodeFunctions';
import { getNormalizedSaleModuleName } from 'utils/formatters/getNormalizedSaleModuleName';
import { saleSortOptions } from 'utils/options';
import { getSaleValueFromState } from 'utils/state-managment/sale/saleFilter';
import { SaleFilterReturnData } from 'utils/types/Sale';
import { SortType } from 'utils/types/SortType';
import { SavedLocationsType } from 'utils/types/V2Locations';

import Indicator from 'components/atom/Indicator';
import FiltersContentWrapper from 'components/common/FiltersContentWrapper';
import FiltersSale from 'components/sales/FiltersSale';
import SaleOffers from 'components/sales/SaleOffers';
import V2HeaderSection from 'components/V2HeaderSection';

const Container = styled.div`
    padding: 15px 20px 0;
`;

const SalesPage = () => {
    const {
        saleOfferState,
        saleFiltersState,
        handleChange,
        loadSaleOffers,
        clearOffers,
        setLoadingPage,
        stopLoadingPage,
        handleFillFilterFields,
        getSavedFilters
    } = useContext(SaleContext);
    const { aiFilterData } = useParams();

    const decodedData = aiFilterData ? decodeData<SaleFilterReturnData>(aiFilterData) : null;

    const initialSearchDoneRef = useRef(false);

    useEffect(() => {
        initialSearchDoneRef.current = false;

        if (decodedData && Object.keys(decodedData).length > 0) {
            setLoadingPage();

            const hasSavedLocations = decodedData.savedLocations && Object.keys(decodedData.savedLocations).length > 0;

            const savedLocations = hasSavedLocations
                ? decodedData.savedLocations
                : Object.fromEntries(
                    decodedData.locations
                        .filter((loc) => loc.id)
                        .map((loc) => [loc.id, { id: loc.id, label: loc.fullName || '' }])
                ) as SavedLocationsType;

            const preparedData = {
                ...decodedData,
                savedLocations,
                locations: [],
                filterLocations: [],
                searchLocationResult: decodedData.searchLocationResult || [],
                module: decodedData.module
            };

            handleFillFilterFields(preparedData);
            getSavedFilters();
        }
    }, [aiFilterData]);

    useEffect(() => {
        if (initialSearchDoneRef.current) return;
        if (!decodedData || Object.keys(decodedData).length === 0) return;
        if (Object.keys(saleFiltersState.savedLocations).length === 0) return;

        initialSearchDoneRef.current = true;
        loadSaleOffers({
            requestData: getSaleValueFromState(saleFiltersState)
        });
        stopLoadingPage();
    }, [saleFiltersState.savedLocations]);

    const handleChangeSort = (sortType: string) => {
        handleChange('sortType', sortType);
        loadSaleOffers({
            requestData: {
                ...getSaleValueFromState(saleFiltersState),
                sortType: sortType as SortType
            },
            page: 1,
            rows: 25
        });
    };

    const handleCSVFile = useCallback(() => {
        setLoadingPage();
        saleOffersApiRequests
            .getSaleOffersCSVFile(
                getSaleValueFromState(saleFiltersState),
                getNormalizedSaleModuleName(saleFiltersState.module)
            )
            .finally(() => {
                stopLoadingPage();
            });
    }, [saleFiltersState]);

    const handleShowLowerOriginalPriceOffers = () => {
        handleChange(
            'lowerOriginalPrice',
            !saleFiltersState.lowerOriginalPrice
        );
        loadSaleOffers({
            requestData: {
                ...getSaleValueFromState(saleFiltersState),
                lowerOriginalPrice: !saleFiltersState.lowerOriginalPrice
            },
            page: 1,
            rows: 25
        });
    };

    const handleShowUnblockedOffers = () => {
        handleChange(
            'unlockedAdvertisements',
            !saleFiltersState.unlockedAdvertisements
        );
        loadSaleOffers({
            requestData: {
                ...getSaleValueFromState(saleFiltersState),
                unlockedAdvertisements:
                    !saleFiltersState.unlockedAdvertisements
            },
            page: 1,
            rows: 25
        });
    };

    return (
        <div>
            <V2HeaderSection
                title={'Ogłoszenia sprzedaży'}
                offersCount={saleOfferState.saleOfferList?.totalElements}
                sortType={
                    saleOfferState.saleOfferList.content?.length
                        ? saleFiltersState.sortType
                        : undefined
                }
                sortOptions={saleSortOptions}
                handleChangeSort={handleChangeSort}
                handleBackToFilters={
                    saleOfferState.saleOfferList.content?.length ||
                    saleOfferState.saleOfferList.empty
                        ? clearOffers
                        : undefined
                }
                handleCSVClick={handleCSVFile}
                handleShowLowerOriginalPriceOffers={
                    handleShowLowerOriginalPriceOffers
                }
                showLowerOriginalPriceOffers={
                    saleFiltersState.lowerOriginalPrice
                }
                showLowerPriceCheckInput={
                    saleOfferState.saleOfferList?.totalElements &&
                        saleOfferState.saleOfferList?.totalElements > 0 ||
                    saleOfferState.saleOfferList?.empty
                }
                handleShowUnblockedOffers={handleShowUnblockedOffers}
                showUnblockedOffers={saleFiltersState.unlockedAdvertisements}
                showUnblockedOffersInput={
                    saleOfferState.saleOfferList?.totalElements &&
                        saleOfferState.saleOfferList?.totalElements > 0 ||
                    saleOfferState.saleOfferList?.empty
                }
            />
            <FiltersContentWrapper
                className={clsx('offers-list', {
                    active:
                        saleOfferState.saleOfferList?.content &&
                            saleOfferState.saleOfferList?.content.length > 0 ||
                        saleOfferState.saleOfferList?.empty
                })}
            >
                <Container id="filters">
                    <FiltersSale />
                </Container>
                <SaleOffers />
                {saleOfferState.isLoading && <Indicator />}
            </FiltersContentWrapper>
        </div>
    );
};

export default SalesPage;
